import type {
  SelectModeIframeMessage,
  SelectModeParentMessage,
} from "./generated/selectModeIframeRpc";

import { z } from "zod";

import {
  displayModeSchema,
  selectableDataSchema,
  selectedElementSchema,
  selectModeBoundingRectSchema,
  selectModeClickPositionSchema,
  selectModeFormatCommandSchema,
  selectModeOptimisticUpdatePayloadSchema,
  selectModeSelectionRectSchema,
  selectModeSelectionTypeSchema,
  selectModeTiptapActiveMarksSchema,
} from "./selectMode";

export const SELECT_MODE_IFRAME_RPC_CHANNEL_ID =
  "replo:select-mode-iframe-rpc" as const;
export const SELECT_MODE_IFRAME_RPC_CHANNEL_VERSION = 1 as const;

export const iframeRuntimeBuildMetadataSchema = z
  .object({
    packageName: z.string(),
    version: z.string(),
    branch: z.string().nullable(),
    commit: z.string().nullable(),
    builtAt: z.string().nullable(),
  })
  .describe("IframeRuntimeBuildMetadata");

export const websiteBuilderToCanopyMethodConfig = {
  symbolicateSelectedElement: {
    inputSchema: selectedElementSchema,
    outputSchema: selectedElementSchema,
    timeoutMs: 1_500,
  },
  // Client-side transition command; fire-and-forget because success is observed as the `replo:navigated` echo.
  navigateToRoute: {
    inputSchema: z.object({ pathname: z.string() }),
    outputSchema: z.void(),
  },
  setDisplayMode: {
    inputSchema: z.object({ displayMode: displayModeSchema }),
    outputSchema: z.void(),
  },
  // Declarative push of the parent's current selection type into the
  // canopy iframe. Transitioning to `none` is canopy's signal to hide
  // its in-iframe selection frame and drop any in-progress drag —
  // there's no separate "clear" message because selection state always
  // flows through agentUiState.
  setSelectionType: {
    inputSchema: z.object({ selectionType: selectModeSelectionTypeSchema }),
    outputSchema: z.void(),
  },
  applyOptimisticUpdate: {
    inputSchema: z.object({
      editId: z.string(),
      payload: selectModeOptimisticUpdatePayloadSchema,
      targetSelector: z.string(),
      targetMode: z.enum(["primary", "group"]),
    }),
    outputSchema: z.void(),
  },
  markCommitted: {
    inputSchema: z.object({
      editId: z.string(),
      sessionId: z.string(),
    }),
    outputSchema: z.void(),
  },
  resolveSessionOverrides: {
    inputSchema: z.object({
      sessionId: z.string(),
      outcome: z.enum(["persisted", "failed"]),
    }),
    outputSchema: z.void(),
  },
  revertOverride: {
    inputSchema: z.object({
      editId: z.string(),
    }),
    outputSchema: z.void(),
  },
  enterEditMode: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  exitEditMode: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  formatCommand: {
    inputSchema: z.object({
      command: selectModeFormatCommandSchema,
    }),
    outputSchema: z.void(),
  },
};

export const canopyToWebsiteBuilderMethodConfig = {
  requestInlineTextEdit: {
    inputSchema: z.object({
      editId: z.string(),
      element: selectedElementSchema,
    }),
    outputSchema: z.object({ allowed: z.boolean() }),
    timeoutMs: 5_000,
  },
  iframeReady: {
    inputSchema: iframeRuntimeBuildMetadataSchema.optional(),
    outputSchema: z.void(),
  },
  elementSelected: {
    inputSchema: z.object({
      data: selectableDataSchema,
    }),
    outputSchema: z.void(),
  },
  hoverElement: {
    inputSchema: z.object({
      rect: selectModeBoundingRectSchema.nullable(),
    }),
    outputSchema: z.void(),
  },
  selectedElementRectUpdate: {
    inputSchema: z.object({
      rect: selectModeBoundingRectSchema,
      clickPosition: selectModeClickPositionSchema,
    }),
    outputSchema: z.void(),
  },
  selectedAreaRectUpdate: {
    inputSchema: z.object({
      rect: selectModeBoundingRectSchema,
    }),
    outputSchema: z.void(),
  },
  dragRectUpdated: {
    inputSchema: z.object({
      dragRect: selectModeBoundingRectSchema.nullable(),
      selectionRects: z.array(selectModeSelectionRectSchema),
    }),
    outputSchema: z.void(),
  },
  areaSelected: {
    inputSchema: z.object({
      rect: selectModeBoundingRectSchema,
      items: z.array(selectableDataSchema),
    }),
    outputSchema: z.void(),
  },
  textCommitted: {
    inputSchema: z.object({
      editId: z.string(),
      html: z.string(),
      json: z.record(z.string(), z.unknown()),
      originalInnerHTML: z.string(),
    }),
    outputSchema: z.void(),
  },
  editorStateUpdate: {
    inputSchema: z.object({
      activeMarks: selectModeTiptapActiveMarksSchema,
      // Whether an inline text edit is currently open. Lets the parent show the
      // inline formatting controls (and live mark state) only while editing.
      isEditing: z.boolean(),
    }),
    outputSchema: z.void(),
  },
  fastRefreshComplete: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
};

// Thin Zod unions for typegen + the editor→miniapp relay hop. Only void
// methods appear here (valued methods use channel.call directly).
export const selectModeParentMessageSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("setDisplayMode"),
      data: websiteBuilderToCanopyMethodConfig.setDisplayMode.inputSchema,
    }),
    z.object({
      method: z.literal("setSelectionType"),
      data: websiteBuilderToCanopyMethodConfig.setSelectionType.inputSchema,
    }),
    z.object({
      method: z.literal("applyOptimisticUpdate"),
      data: websiteBuilderToCanopyMethodConfig.applyOptimisticUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("markCommitted"),
      data: websiteBuilderToCanopyMethodConfig.markCommitted.inputSchema,
    }),
    z.object({
      method: z.literal("resolveSessionOverrides"),
      data: websiteBuilderToCanopyMethodConfig.resolveSessionOverrides
        .inputSchema,
    }),
    z.object({
      method: z.literal("revertOverride"),
      data: websiteBuilderToCanopyMethodConfig.revertOverride.inputSchema,
    }),
    z.object({
      method: z.literal("enterEditMode"),
    }),
    z.object({
      method: z.literal("exitEditMode"),
    }),
    z.object({
      method: z.literal("formatCommand"),
      data: websiteBuilderToCanopyMethodConfig.formatCommand.inputSchema,
    }),
  ])
  .describe("SelectModeParentMessage");

export const selectModeIframeMessageSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("iframeReady"),
      data: iframeRuntimeBuildMetadataSchema.optional(),
    }),
    z.object({
      method: z.literal("elementSelected"),
      data: canopyToWebsiteBuilderMethodConfig.elementSelected.inputSchema,
    }),
    z.object({
      method: z.literal("hoverElement"),
      data: canopyToWebsiteBuilderMethodConfig.hoverElement.inputSchema,
    }),
    z.object({
      method: z.literal("selectedElementRectUpdate"),
      data: canopyToWebsiteBuilderMethodConfig.selectedElementRectUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("selectedAreaRectUpdate"),
      data: canopyToWebsiteBuilderMethodConfig.selectedAreaRectUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("dragRectUpdated"),
      data: canopyToWebsiteBuilderMethodConfig.dragRectUpdated.inputSchema,
    }),
    z.object({
      method: z.literal("areaSelected"),
      data: canopyToWebsiteBuilderMethodConfig.areaSelected.inputSchema,
    }),
    z.object({
      method: z.literal("textCommitted"),
      data: canopyToWebsiteBuilderMethodConfig.textCommitted.inputSchema,
    }),
    z.object({
      method: z.literal("editorStateUpdate"),
      data: canopyToWebsiteBuilderMethodConfig.editorStateUpdate.inputSchema,
    }),
    z.object({
      method: z.literal("fastRefreshComplete"),
    }),
  ])
  .describe("SelectModeIframeMessage");

export type SelectModeParentMessageInputOf<
  MethodType extends SelectModeParentMessage["method"],
> =
  Extract<SelectModeParentMessage, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;

export type SelectModeIframeMessageInputOf<
  MethodType extends SelectModeIframeMessage["method"],
> =
  Extract<SelectModeIframeMessage, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;
